import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '@/lib/api';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // null = checking, false = logged out, object = logged in
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // Bootstrap session from cookie
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const loginAs = useCallback(
    async (personaId) => {
      // No credentials in the browser: the backend mints a short-lived,
      // read-only, demo-tenant-scoped session for the requested persona.
      const { data } = await api.post('/auth/demo-login', { persona: personaId });
      setUser(data.user);
      return data.user;
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore network errors on logout
    }
    setUser(false);
  }, []);

  // Iter 41.39 — re-fetch /auth/me after a self-service mutation that
  // changes the user document (e.g. password rotation clearing the
  // must_change_password gate). Returns the refreshed user or null.
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AppContext.Provider value={{ user, setUser, authReady, login, loginAs, logout, refreshUser, formatApiError }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
